import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../lib/api";

/**
 * Prikaz kad backend vrati 403 "modul nije uključen u vaš paket"
 * (ModuleAccessGuard/EntitlementService, vidi ApiErrorResponse.moduleAccess)
 * - vidi razgovor "generic CTA za modul nije uključen". Zamenjuje sirovu
 * crvenu poruku greške svuda gde postoji RequireModule guard: nudi
 * "Aktiviraj dodatak" (ako je addonAvailable) koje generiše ponudu i vodi
 * korisnika na Moji računi da je plati, ili "Nadogradi paket" ako modul
 * NIJE dostupan kao samostalan dodatak (mora viši paket, vidi
 * minimumPackage).
 *
 * Koristiti umesto ručnog ispisivanja error.response.message kad god
 * error.response.errorCode === "MODULE_NOT_AVAILABLE" (ili "NO_SUBSCRIPTION"/
 * "SUBSCRIPTION_INACTIVE" - tada nema addon/paket CTA, samo poruka).
 */
export function ModuleGateNotice({ error, moduleKey }: { error: unknown; moduleKey: string }) {
  const navigate = useNavigate();
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const isModuleError = error instanceof ApiError && error.response.errorCode === "MODULE_NOT_AVAILABLE";
  const message = error instanceof ApiError ? error.response.message : "Modul nije dostupan.";
  const moduleAccess = error instanceof ApiError ? error.response.moduleAccess : undefined;

  const handleActivate = async () => {
    setPurchasing(true);
    setPurchaseError(null);
    try {
      await api.purchaseAddon(moduleKey);
      // Ponuda je generisana i odmah poslata (SENT) klijentu - "Moji računi"
      // je gde je vidi i plaća (isto kao "Promeni paket"), plaćanje je
      // aktivacija (vidi registerPayment/applyAddonPurchase na backendu).
      navigate("/moji-racuni");
    } catch (err) {
      setPurchaseError(err instanceof ApiError ? err.response.message : "Aktivacija dodatka nije uspela.");
      setPurchasing(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-app-bg px-5 py-6 text-center">
      <Lock className="h-6 w-6 text-slate-400" aria-hidden />
      <p className="max-w-md text-sm text-slate-600">{message}</p>

      {isModuleError && moduleAccess?.addonAvailable && (
        <Button onClick={handleActivate} disabled={purchasing}>
          {purchasing ? "Kreiranje ponude…" : "Aktiviraj dodatak"}
        </Button>
      )}

      {isModuleError && !moduleAccess?.addonAvailable && moduleAccess?.minimumPackage && (
        <Button onClick={() => navigate("/moj-paket")}>
          Nadogradi na paket "{moduleAccess.minimumPackage.name}"
        </Button>
      )}

      {purchaseError && <p className="text-xs text-red-600">{purchaseError}</p>}
    </div>
  );
}
